import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Footer from "@/components/Footer";
import ChatPanel from "@/components/ChatPanel";
import { ExternalLink } from "lucide-react";

const TIMELINE = [
  { key: "In attesa", label: "Ricevuta" },
  { key: "In lavorazione", label: "In lavorazione" },
  { key: "Preventivo inviato", label: "Preventivo inviato" },
  { key: "Confermato", label: "Confermato" },
];

function Timeline({ status }) {
  const idx = TIMELINE.findIndex((s) => s.key === status);
  const rejected = status === "Rifiutato";
  return (
    <div className="flex items-center gap-1 mt-4" data-testid="request-timeline">
      {TIMELINE.map((s, i) => {
        const done = !rejected && idx >= i;
        const active = !rejected && idx === i;
        return (
          <div key={s.key} className="flex-1 flex flex-col items-center">
            <div
              className={`w-full h-1 rounded-full transition-colors ${
                done ? "bg-[#E5A93C]" : "bg-[#F7F4EE]/10"
              }`}
            />
            <div
              className={`text-[10px] mt-2 tracking-widest uppercase ${
                active ? "text-[#E5A93C]" : done ? "text-[#F7F4EE]/80" : "text-[#A39E93]"
              }`}
            >
              {s.label}
            </div>
          </div>
        );
      })}
      {rejected && (
        <span className="ml-3 text-xs text-red-300 border border-red-400/40 rounded-full px-3 py-1">
          Rifiutato
        </span>
      )}
    </div>
  );
}

export default function MyRequests() {
  const { user, ready } = useAuth();
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== "client") return;
    api
      .get("/quote-requests")
      .then((r) => setReqs(r.data))
      .finally(() => setLoading(false));
  }, [user]);

  if (!ready) return <div className="pt-28 text-center text-[#A39E93]">Caricamento…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/le-mie-richieste" }} replace />;

  return (
    <div className="pt-28 pb-16">
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="av-eyebrow">Area cliente</span>
        <h1 className="font-serif-display text-4xl sm:text-5xl mt-3">Le mie richieste</h1>

        {loading ? (
          <p className="text-[#A39E93] mt-10">Caricamento…</p>
        ) : reqs.length === 0 ? (
          <p className="text-[#A39E93] mt-10">Non hai ancora inviato richieste.</p>
        ) : (
          <div className="mt-10 space-y-5" data-testid="my-requests-list">
            {reqs.map((r) => (
              <article key={r.id} className="av-card p-6" data-testid={`my-request-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif-display text-2xl">{r.event_name}</h3>
                    <p className="text-sm text-[#A39E93] mt-1">
                      {r.city} {r.venue && `· ${r.venue}`} {r.event_date && `· ${r.event_date}`}
                    </p>
                  </div>
                  <span className="text-xs uppercase tracking-widest text-[#E5A93C]">
                    Inviata il {new Date(r.created_at).toLocaleDateString("it-IT")}
                  </span>
                </div>
                <Timeline status={r.status} />
                {r.admin_note && (
                  <div className="mt-4 border-l-2 border-[#E5A93C] pl-3">
                    <div className="av-eyebrow text-[10px]">Nota NMA</div>
                    <p className="text-sm text-[#F7F4EE]/85 mt-1 whitespace-pre-line">
                      {r.admin_note}
                    </p>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mt-4">
                  {r.artists.map((a) => (
                    <span key={a.id} className="text-xs px-2 py-1 rounded-full border av-hairline">
                      🎤 {a.name}
                    </span>
                  ))}
                  {r.shows.map((s) => (
                    <span key={s.id} className="text-xs px-2 py-1 rounded-full border av-hairline">
                      🎭 {s.title}
                    </span>
                  ))}
                </div>
                {r.share_token && (
                  <a
                    href={`/preventivo/${r.share_token}`}
                    target="_blank"
                    rel="noreferrer"
                    className="av-pill av-pill-ghost text-xs mt-4"
                    data-testid={`open-public-quote-${r.id}`}
                  >
                    <ExternalLink size={12} /> Apri preventivo interattivo
                  </a>
                )}
                <ChatPanel requestId={r.id} currentUserRole="client" />
              </article>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
